// ui/skillInfo.js
// Skill info tooltips - provides quick hints about what each skill does

import { SKILLS } from '../game/constants.js';

/**
 * Skill descriptions for info tooltips
 * Each entry has a short description and what the skill connects to
 */
const SKILL_DESCRIPTIONS = {
    [SKILLS.MINING]: {
        desc: 'Mine rocks to gather ores and gems.',
        usedFor: 'Smithing (bars, weapons, armor)'
    },
    [SKILLS.SMITHING]: {
        desc: 'Smelt ores into bars and forge equipment.',
        usedFor: 'Crafting, Woodworking (arrowheads)'
    },
    [SKILLS.WOODCUTTING]: {
        desc: 'Chop trees to gather logs.',
        usedFor: 'Pyre, Woodworking, Architecting'
    },
    [SKILLS.PYRE]: {
        desc: 'Burn logs to produce ashes and increase perception.',
        usedFor: 'Reveals hidden depths in combat zones'
    },
    [SKILLS.WOODWORKING]: {
        desc: 'Craft bows, arrows, and magical staves.',
        usedFor: 'Ranged and Magic combat'
    },
    [SKILLS.FISHING]: {
        desc: 'Catch fish from various fishing spots.',
        usedFor: 'Cooking (food for combat)'
    },
    [SKILLS.COOKING]: {
        desc: 'Cook fish into food that heals during combat.',
        usedFor: 'Combat healing, Auto-eat system',
        note: 'Soon: Enhance with crops for bonus effects'
    },
    [SKILLS.FARMING]: {
        desc: 'Plant seeds and wait for passive growth.',
        usedFor: 'Alchemy (herbs), Woodcutting (tree logs)'
    },
    [SKILLS.ALCHEMY]: {
        desc: 'Clean herbs and brew potions for combat buffs.',
        usedFor: 'Combat buffs and healing'
    },
    [SKILLS.PILFERING]: {
        desc: 'Pickpocket NPCs for gold.',
        usedFor: 'Guild unlocks passive material gathering'
    },
    [SKILLS.CRAFTING]: {
        desc: 'Create jewelry, armor, and equipment.',
        usedFor: 'Combat gear (defense, stats)'
    },
    [SKILLS.GLYPHBINDING]: {
        desc: 'Gather essence and craft elemental glyphs.',
        usedFor: 'Magic spells (ammo), Woodworking (staves)'
    },
    [SKILLS.GATHERER]: {
        desc: 'Explore zones for pet food and rare materials.',
        usedFor: 'Husbandry (feeding and evolving pets)'
    },
    [SKILLS.HUSBANDRY]: {
        desc: 'Manage pets that run contracts passively.',
        usedFor: 'Passive resource generation'
    },
    [SKILLS.BOUNTY]: {
        desc: 'Accept and complete contracts for rewards.',
        usedFor: 'Bonus XP, gold, and progression'
    },
    [SKILLS.COMBAT]: {
        desc: 'Fight monsters for XP, gold, and drops.',
        usedFor: 'Gear progression, hides, bones'
    },
    [SKILLS.ARCHITECTING]: {
        desc: 'Build blueprints and milestones for upgrades.',
        usedFor: 'Bank slots, pet chest, guild expansion'
    }
};

/**
 * Render the skill info button HTML
 * @param {string} skillId - The skill ID from SKILLS enum
 * @returns {string} HTML string for the info button
 */
export function renderSkillInfo(skillId) {
    const info = SKILL_DESCRIPTIONS[skillId];
    if (!info) return '';

    // Build tooltip content
    let tooltipContent = info.desc;
    if (info.usedFor) {
        tooltipContent += `\n→ ${info.usedFor}`;
    }
    if (info.note) {
        tooltipContent += `\n★ ${info.note}`;
    }

    // Escape for HTML attribute
    const escapedContent = tooltipContent
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    return `<span class="skill-info-btn" data-tooltip="${escapedContent}">ⓘ</span>`;
}

/**
 * Get skill description object
 * @param {string} skillId - The skill ID from SKILLS enum
 * @returns {object|null} Description object or null
 */
export function getSkillDescription(skillId) {
    return SKILL_DESCRIPTIONS[skillId] || null;
}

export default {
    renderSkillInfo,
    getSkillDescription,
    SKILL_DESCRIPTIONS
};
